<template>
  <div class="condition-report-request-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :search="tableData.search" :fields="tableFields"
                   :hide-per-page-selector="hidePerPageSelector"
                   :enable-simple-search="false" :index="false" requires-min-height
                   :display-checkbox="checkDisplayCheckbox"
                   :checkboxes="$hasPermission('export_conditionreport')"
                   :selectable="$hasPermission('export_conditionreport')"
                   @paramsChanged="getTableData" :filter-toggle-string="filterToggleString"
                   :extra-data="extraData"
                   show-toggle-columns
                   @selectedItemsChange="onSelectedItemsChange"
                   @on-column-save="updateToggleColumns">
      <template v-slot:header-buttons="{selectedItems}" v-if="$hasPermission('export_conditionreport') && !hideTopActions">
        <b-dropdown class="btn-table-actions" :text="$t('Actions')" variant="primary"
                    v-permission="'export_conditionreport'">
          <b-dropdown-item :disabled="tableData.loading || !selectedItems.size"
                           @click="onBatchSend(selectedItems)">
            {{ $t("Batch Send") }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="condition-report-request-table">
          <condition-report-request-search :update-search-model="updateSearchModel" :get-table-data="getTableData"
                                           :vuex-search-model="tableData.searchModel"
                                           :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(status)" slot-scope="{item}">
        <span v-as-status="`condition_request_${item.status}`" v-if="item.status">
          {{ item.status | removeUnderscores | toUpperCaseEveryWord | translate }}
        </span>
      </template>
      <template slot="cell(created_at)" slot-scope="{item}">
        {{ item.created_at | toDateTime }}
      </template>
      <template slot="cell(created_by)" slot-scope="{item}">
        {{ item.created_by ? item.created_by.display_name : '' }}
      </template>
      <template slot="cell(sent_at)" slot-scope="{item}">
        {{ item.sent_at | toDateTime }}
      </template>
      <template slot="cell(sent_by)" slot-scope="{item}">
        {{ item.sent_by ? item.sent_by.display_name : '' }}
      </template>
      <template slot="cell(rejected_at)" slot-scope="{item}">
        {{ item.rejected_at | toDateTime }}
      </template>
      <template slot="cell(rejected_by)" slot-scope="{item}">
        {{ item.rejected_by ? item.rejected_by.display_name : '' }}
      </template>
      <template slot="cell(message)" slot-scope="{item: item, toggleDetails}">
        <div class="toggle-row">
            <div class="content" >
                {{ item.message }}
            </div>
            <a href="javascript:void(0)" @click="toggleDetails" v-if="item.message && item.message.length > 20">
                view more
            </a>
        </div>
      </template>
      <template #row-details="{item}">
        <div class="toggle-row__content">
          {{ item.message }}
        </div>
      </template>

      <template slot="cell(customer)" slot-scope="{item}">
        <router-link v-if="item.customer && $hasPermission('view_customer')"
                     :to="{ name: 'customers.dashboard', params: { id: item.customer_uuid }}">
          {{ item.customer.display_name }}
        </router-link>
        <p v-else-if="item.customer"> {{ item.customer.display_name }} </p>
      </template>
      <template slot="cell(inventory)" slot-scope="{item}">
        <router-link v-if="$hasPermission('view_inventoryitem')"
                     :to="{name: 'inventory.dashboard', params: {id: item.inventory.uuid}}">
          {{ item.inventory.stock_no }} {{ item.inventory.short_description | striptags | truncate(50) }}
        </router-link>
        <span v-else>
          {{ item.inventory.stock_no }} {{ item.inventory.short_description | striptags | truncate(50) }}
        </span>
      </template>
      <template slot="cell(condition_report)" slot-scope="{item}">
        <template v-if="item.condition_report">
          [{{ item.condition_report.version }}{{ item.condition_report.version_suffix }}]
          - {{ item.condition_report.title }}
        </template>
      </template>
      <template slot="cell(actions)" slot-scope="{item}">
        <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v" class="icon"/>
          </template>
          <b-dd-item @click="viewActivityLog(item)">
            <font-awesome-icon icon="history" style="font-size: .9em" class="mr-1"/>
            {{ $t('Activity Log') }}
          </b-dd-item>
          <b-dd-item @click="send(item)" :disabled="item.status !== 'pending'">
            <font-awesome-icon icon="paper-plane" style="font-size: .9em" class="mr-1"/>
            {{ $t('Send') }}
          </b-dd-item>
          <b-dd-item @click="onRemoveClick(item)" v-if="item.status === 'pending'">
            <font-awesome-icon icon="times" style="font-size: .9em" class="mr-1"/>
            {{ $t('Reject') }}
          </b-dd-item>
        </b-dropdown>
      </template>
      <template slot="cell(sale_no)" slot-scope="{item}">
        <template v-if="item.reservation">
          {{ item.reservation.auction.sale_no }}
        </template>
      </template>
      <template slot="cell(sale_title)" slot-scope="{item}">
        <template v-if="item.reservation">
          {{ item.reservation.auction.title | masterLanguage }}
        </template>
      </template>
      <template slot="cell(lot_no)" slot-scope="{item}">
        <template v-if="item.reservation">
          {{ item.reservation.lot_no }}
        </template>
      </template>
      <template slot="cell(department)" slot-scope="{item}">
        <template v-if="item.inventory && item.inventory.department">
          {{ item.inventory.department.name | masterLanguage }}
        </template>
      </template>
      <template slot="cell(category)" slot-scope="{item}">
        <template v-if="item.inventory && item.inventory.category">
          {{ item.inventory.category.name | masterLanguage }}
        </template>
      </template>
    </artisio-table>
    <condition-report-request-activities-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import {
    ACTION_CREATE_NEW_CONDITION_REPORT,
    ACTION_EDIT_CONDITION_REPORT,
    ACTION_REMOVE_CONDITION_REPORT,
    CONDITION_REPORT_REQUESTS_TABLE_NAME
  } from "@/modules/constants";
  import ConditionReportRequestSearch from "@/modules/condition-report-requests/ConditionReportRequestSearch.vue";
  import _ from "lodash";
  import ConditionReportRequestActivitiesModal
    from "@/modules/condition-report-requests/ConditionReportRequestActivitiesModal.vue";

  const {mapActions, mapState} = createNamespacedHelpers('conditionReportRequests');
  const {mapActions: activitiesMapActions} = createNamespacedHelpers('activities');

  export default {
    name: "ConditionReportRequestsTable",
    components: {
      ConditionReportRequestActivitiesModal,
      ConditionReportRequestSearch, SearchCollapse, ArtisioTable
    },
    props: {
      excludeColumns: {
        type: Array,
        default: () => [],
      },
      excludeSearchFields: {
        type: Array,
        default: () => [],
      },
      hidePerPageSelector: {
        type: Boolean,
        default: false
      },
      inventory: {
        type: Object,
        default: null
      },
      sale: {
        type: Object,
        default: null
      },
      source: {
        type: String,
        default: 'sale'
      },
      hideActions: {
        type: [Boolean, Array],
        default: () => []
      },
      changePermission: {
        type: [Boolean, String],
        default: false
      },
      deletePermission: {
        type: [Boolean, String],
        default: false
      },
      filterToggleString: {
        type: String,
        default: 'condition-report-request-table'
      },
      hideTopActions: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        ACTION_CREATE_NEW_CONDITION_REPORT: ACTION_CREATE_NEW_CONDITION_REPORT,
        ACTION_EDIT_CONDITION_REPORT: ACTION_EDIT_CONDITION_REPORT,
        ACTION_REMOVE_CONDITION_REPORT: ACTION_REMOVE_CONDITION_REPORT,
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        tableSource: state => state.tableSource,
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
      }),

      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
            .filter(field => !this.excludeColumns.includes(field.key) && field.isVisible)
      },

      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: `${CONDITION_REPORT_REQUESTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'updateTableSource',
        'updateConditionReportRequestPartially', 'updateSearchModel', 'updateToggleColumns', 'showSendModal']),
      ...activitiesMapActions(['showActivitiesModal', 'setActivitiesParams']),
      hideAction(actionName) {
        return this.hideActions.includes(actionName)
      },
      async onRemoveClick(item) {
        this.$confirm(
            this.$i18n.t("Are you sure you want to reject following condition report request?"),
            this.$i18n.t('Condition Report request was successfully rejected'),
            this.updateConditionReportRequestPartially,
            {uuid: item.uuid, status: 'rejected'}
        );
      },
      viewActivityLog(item) {
        this.setActivitiesParams({object: item, source: 'condition_report', limit: 9});
        this.showActivitiesModal();
      },
      send(item) {
        this.showSendModal({
          object: {request_uuids: [item.uuid], inventory_uuid: item.inventory_uuid, items: [item]},
          source: 'requests'
        });
      },
      onBatchSend(selectedItems) {
        const uniqueInventories = new Set(); // Create a new Set to store unique 'id' values

        for (const obj of selectedItems) {
          uniqueInventories.add(obj.inventory_uuid);
        }
        if (uniqueInventories.size > 1) {
          this.$errorDialog(this.$i18n.t('You can only send report to single inventory item at a time.'));
          return;
        }
        const uuids = Array.from(selectedItems).map(i => i.uuid);
        this.showSendModal({
          object: {
            request_uuids: uuids,
            inventory_uuid: Array.from(uniqueInventories)[0],
            items: Array.from(selectedItems)
          },
          source: 'requests'
        });
      },
      checkDisplayCheckbox(item) {
        return item.status === 'pending';
      },
      onSelectedItemsChange(selectedItems) {
        this.$emit('selectedItemsChange', selectedItems);
      }
    },
    mounted() {
      this.getTableData();
    },
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";
  .toggle-row {
    display: flex;
    max-width: 300px;

    a {
      margin-left: 1em;
    }
  }

  .content {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .toggle-row__content {
    white-space: normal;
    max-width: 1000px;
    padding: 20px 0 20px 40px;
  }
</style>

